"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import React from "react";
import appLinks from '@/../config/appLinks.json'; // Import app links

// SVG Icon
const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const HeroSection = () => {
  // Variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.03
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.15
      }
    }
  };

  return (
    <motion.div 
      className="relative mb-24 text-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      {/* Ambient lighting effects removed */}
      
      {/* Noise texture overlay */}
      <div className="absolute inset-0 opacity-[0.03] mix-blend-soft-light pointer-events-none bg-repeat">
        <div className="absolute inset-0 backdrop-filter backdrop-grayscale"></div>
      </div>
      
      {/* Decorative floating elements */}
      <div className="absolute top-[20%] left-[15%] w-8 h-8 rounded-full border border-blue-500/10 opacity-20 animate-float-slow pointer-events-none"></div>
      <div className="absolute top-[60%] right-[10%] w-12 h-12 rounded-full border border-indigo-500/10 opacity-20 animate-float-medium pointer-events-none"></div>
      <div className="absolute bottom-[10%] left-[30%] w-6 h-6 rounded-sm border border-sky-500/10 opacity-20 animate-float-fast rotate-45 pointer-events-none"></div>
      
      <div className="relative z-10 container mx-auto px-4">
        <motion.div
          className="mb-5 inline-flex items-center justify-center px-5 py-2 rounded-full bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border border-blue-700/20 backdrop-blur-sm"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, delay: 0.05 }}
        >
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#38bdf8] to-[#818cf8] font-medium text-sm tracking-wide">INTELLIGENT MEETING ASSISTANT</span>
        </motion.div>

        <motion.h1 
          className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 tracking-tight leading-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, delay: 0.1 }}
          style={{ 
            letterSpacing: "-0.015em",
            textShadow: "0px 2px 15px rgba(56, 189, 248, 0.1)"
          }}
        >
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-300">
            MeetMinutes – The AI Notetaker<br />
            That Actually{" "}
          </span>
          <span className="relative">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#38bdf8] to-[#8B5CF6]">Listens</span>
            <span className="absolute -bottom-1 left-0 right-0 h-[3px] bg-gradient-to-r from-[#38bdf8] to-[#8B5CF6] opacity-50 rounded-full"></span>
          </span>
        </motion.h1>

        <motion.h2 
          className="text-xl md:text-2xl font-medium text-slate-300 mb-12 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2, delay: 0.13 }}
          style={{ lineHeight: "1.4" }}
        >
          Why Settle for Just Notes When You Can Capture Meaning?
        </motion.h2>

        <motion.div 
          className="mx-auto max-w-3xl mb-14"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.13, duration: 0.2 }}
        >
          <p className="text-lg text-slate-300 mb-8 leading-relaxed">
            MeetMinutes isn&apos;t just another AI that transcribes – It&apos;s your strategic meeting companion that:
          </p>

          <motion.div 
            className="space-y-6 text-left max-w-xl mx-auto"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div 
              className="flex items-center transform transition-transform duration-300 hover:translate-x-1" 
              variants={itemVariants}
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-900/40 to-indigo-900/40 border border-blue-500/20 flex items-center justify-center mr-3 shadow-md shadow-blue-500/5">
                <CheckIcon />
              </div>
              <span className="text-lg text-gray-200">Understands discussion context and nuance</span>
            </motion.div>
            <motion.div 
              className="flex items-center transform transition-transform duration-300 hover:translate-x-1" 
              variants={itemVariants}
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-900/40 to-indigo-900/40 border border-blue-500/20 flex items-center justify-center mr-3 shadow-md shadow-blue-500/5">
                <CheckIcon />
              </div>
              <span className="text-lg text-gray-200">Captures decisions with ownership and deadlines</span>
            </motion.div>
            <motion.div 
              className="flex items-center transform transition-transform duration-300 hover:translate-x-1" 
              variants={itemVariants}
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-900/40 to-indigo-900/40 border border-blue-500/20 flex items-center justify-center mr-3 shadow-md shadow-blue-500/5">
                <CheckIcon />
              </div>
              <span className="text-lg text-gray-200">Tracks action items automatically</span>
            </motion.div>
            <motion.div 
              className="flex items-center transform transition-transform duration-300 hover:translate-x-1" 
              variants={itemVariants}
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-900/40 to-indigo-900/40 border border-blue-500/20 flex items-center justify-center mr-3 shadow-md shadow-blue-500/5">
                <CheckIcon />
              </div>
              <span className="text-lg text-gray-200">Seamlessly integrates with your workflow tools</span>
            </motion.div>
          </motion.div>
        </motion.div>

        <p className="text-xl font-medium text-transparent bg-clip-text bg-gradient-to-r from-slate-200 to-slate-300 max-w-2xl mx-auto leading-relaxed mb-12">
          While others give you a transcript, we give you clarity and direction.
        </p>
        
        <motion.div 
          className="flex justify-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.15 }}
        >
          <Link href={appLinks.loginLink} legacyBehavior>
            <a className="group relative px-8 py-4 rounded-full text-white font-semibold bg-gradient-to-r from-[#2563EB] to-[#4F46E5] hover:from-[#1D4ED8] hover:to-[#4338CA] transition-all duration-150 shadow-lg hover:shadow-blue-500/30 hover:shadow-xl text-lg min-w-[200px] text-center overflow-hidden">
              <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-blue-600/30 to-indigo-600/30 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-150"></span>
              <span className="relative flex items-center justify-center">
                Try MeetMinutes Free
                <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-150" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </span>
            </a>
          </Link>
        </motion.div>
      </div>

      <style jsx global>{`
        @keyframes float-slow {
          0% { transform: translateY(0) rotate(0); }
          50% { transform: translateY(-15px) rotate(5deg); }
          100% { transform: translateY(0) rotate(0); }
        }
        @keyframes float-medium {
          0% { transform: translateY(0) rotate(0); }
          50% { transform: translateY(-10px) rotate(-5deg); }
          100% { transform: translateY(0) rotate(0); }
        }
        @keyframes float-fast {
          0% { transform: translateY(0) rotate(0); }
          50% { transform: translateY(-5px) rotate(10deg); }
          100% { transform: translateY(0) rotate(0); }
        }
        .animate-float-slow {
          animation: float-slow 2s ease-in-out infinite;
        }
        .animate-float-medium {
          animation: float-medium 1.7s ease-in-out infinite;
        }
        .animate-float-fast {
          animation: float-fast 1.3s ease-in-out infinite;
        }
      `}</style>
    </motion.div>
  );
};

export default HeroSection; 